"use client";

import React from "react";
import { Cpu, Cloud, Code, Smartphone, Palette, ShieldCheck, ArrowRight, Zap, Globe, MessageSquare } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const servicesData = [{
  title: "AI Strategy Consulting",
  description: "Develop strategic roadmaps for implementing generative AI, machine learning, and intelligent automation across your organization’s core operations. Cyril Consulting Group helps business leaders identify high-value AI opportunities, align artificial intelligence initiatives with strategic business objectives, and build scalable AI solutions that deliver measurable results.\n\nOur AI consulting services focus on helping organizations transform complex data into actionable insights, enabling smarter decision-making, improved operational efficiency, and new sources of innovation. From evaluating AI use cases to designing implementation strategies, we guide organizations through every stage of their AI adoption journey.\n\nBy combining deep expertise in AI strategy, data infrastructure, automation technologies, and cloud platforms, we help companies deploy artificial intelligence solutions that streamline workflows, optimize processes, and create sustainable competitive advantage in an increasingly data-driven economy.",
  icon: Cpu,
  colSpan: "lg:col-span-2 lg:row-span-2",
  bgGradient: "from-[#008080]/10 to-transparent",
  delay: "0ms"
}, {
  title: "Cloud Transformation",
  description: "Design and implement secure, scalable cloud migration and cloud infrastructure strategies that improve system performance, increase reliability, and reduce long-term IT infrastructure costs.",
  icon: Cloud,
  colSpan: "lg:col-span-1 lg:row-span-1",
  bgGradient: "from-[#1A2A4E]/10 to-transparent",
  delay: "100ms"
}, {
  title: "Automation Consulting",
  description: "Identify opportunities for business process automation and AI-driven workflow optimization that eliminate manual bottlenecks, improve productivity, and allow teams to focus on higher-value work.",
  icon: Zap,
  colSpan: "lg:col-span-1 lg:row-span-1",
  bgGradient: "from-[#008080]/10 to-transparent",
  delay: "200ms"
}, {
  title: "Technology Advisory",
  description: "Provide executive leadership with strategic technology advisory services to guide investments in artificial intelligence, cloud computing, and emerging technologies shaping the future of modern businesses.",
  icon: MessageSquare,
  colSpan: "lg:col-span-1 lg:row-span-1",
  bgGradient: "from-[#D4AF37]/10 to-transparent",
  delay: "300ms"
}, {
  title: "Digital Transformation",
  description: "Modernize legacy systems and implement data-driven digital transformation strategies that integrate business systems, improve operational visibility, and support scalable growth.",
  icon: Globe,
  colSpan: "lg:col-span-1 lg:row-span-1",
  bgGradient: "from-[#1A2A4E]/10 to-transparent",
  delay: "400ms"
}, {
  title: "AI Readiness Assessment",
  description: "Conduct a comprehensive evaluation of your organization's data infrastructure, operational processes, and AI readiness to prepare for successful artificial intelligence implementation.",
  icon: ShieldCheck,
  colSpan: "lg:col-span-1 lg:row-span-1",
  bgGradient: "from-[#D4AF37]/10 to-transparent",
  delay: "500ms"
}];
export default function TailoredDigitalSolutions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="54425" id="strategic-consulting-expertise" ref={ref} className={cn("bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden transition-opacity duration-1000", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#008080]/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#1A2A4E]/5 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#1A2A4E] to-[#1A2A4E]/70">
            Strategic Consulting Expertise
          </h2>
          <p className="text-lg text-muted-foreground font-medium">Cyril Consulting Group provides strategic AI consulting, cloud transformation, workflow automation, and digital transformation advisory services. We help growing businesses, hospitality operators, content creators, and enterprise leaders implement practical technology solutions that improve operational efficiency and deliver measurable ROI.</p>
        </div>

        <TooltipProvider delayDuration={100}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[minmax(200px,auto)]">
            {servicesData.map((service, index) => <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <div data-index={index} className={cn(service.colSpan, "group relative h-full")}>
                    <Card className="h-full bg-white border-slate-200 backdrop-blur-sm hover:bg-white/80 transition-all duration-500 overflow-hidden border hover:border-[#008080]/30 hover:shadow-[0_10px_40px_rgba(0,128,128,0.08)]">
                      {}
                      <div className={cn("absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-br", service.bgGradient)} />
                      
                      <CardHeader className="relative z-10">
                        <div className="mb-4 inline-flex p-3 rounded-xl bg-slate-50 border border-slate-100 group-hover:scale-110 group-hover:bg-white group-hover:border-[#008080]/30 transition-all duration-500 shadow-sm">
                          <service.icon className="size-8 text-[#1A2A4E] group-hover:text-[#008080] transition-colors duration-300" />
                        </div>
                        <CardTitle className="text-xl md:text-2xl font-bold text-[#1A2A4E] group-hover:translate-x-1 transition-transform duration-300">
                          {service.title}
                        </CardTitle>
                      </CardHeader>
                      
                      <CardContent className="relative z-10 flex flex-col justify-between h-full pb-8">
                        <CardDescription className="text-base text-slate-600 group-hover:text-slate-900 transition-colors duration-300 line-clamp-3 md:line-clamp-none">
                          {service.description}
                        </CardDescription>
                        
                        <div className="mt-6 flex items-center text-sm font-bold text-[#D4AF37] opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500 delay-100">
                          Explore Strategy <ArrowRight className="ml-2 size-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-white border-slate-200 shadow-xl text-[#1A2A4E]">
                  <p>Inquire about {service.title}</p>
                </TooltipContent>
              </Tooltip>)}
          </div>
        </TooltipProvider>

        <div className="mt-16 text-center">
          <Link to="/LetsTalkPage">
            <Button size="lg" className="rounded-md px-10 py-7 text-lg bg-[#1A2A4E] hover:bg-[#1A2A4E]/90 text-white shadow-lg shadow-[#1A2A4E]/20 hover:shadow-xl hover:shadow-[#1A2A4E]/30 transition-all duration-300 uppercase tracking-wider font-bold">
              Schedule Consultation
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}